
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/unified-permissions';
import { supabase } from '@/integrations/supabase/client';

interface PreviewResult {
  systemPrompt: string;
  userPrompt: string;
  aiOutput: string;
  model: string;
  tokenUsage: number;
  estimatedCost: number;
  timestamp: string;
  previewType: string;
}

export const InsightPreviewTab: React.FC = () => {
  const { user } = usePermissions();
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [weekStarting, setWeekStarting] = useState<Date>();
  const [previewType, setPreviewType] = useState<'premium' | 'free'>('premium');
  const [model, setModel] = useState<'gpt-4o' | 'gpt-4o-mini'>('gpt-4o');
  const [useSampleData, setUseSampleData] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);

  // Set current user as selected user for security
  useEffect(() => {
    if (user?.id) {
      setSelectedUser(user.id);
    }
  }, [user]);

  // Auto-adjust model based on preview type
  useEffect(() => {
    setModel(previewType === 'premium' ? 'gpt-4o' : 'gpt-4o-mini');
  }, [previewType]);

  const handleGeneratePreview = async () => {
    if (!selectedUser || !weekStarting) {
      toast({
        title: 'Missing Information',
        description: 'Please select a user and week starting date',
        variant: 'destructive',
      });
      return;
    }

    if (!navigator.onLine) {
      toast({
        title: 'Offline Mode',
        description: 'AI insight preview generation requires an internet connection',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('preview-insight', {
        body: JSON.stringify({
          user_id: selectedUser,
          week_start: weekStarting.toISOString().split('T')[0],
        }),
        headers: {
          'X-Preview-Type': previewType === 'premium' ? 'premium' : 'free',
          'X-Selected-Model': model,
        },
      });

      if (error) throw error;

      setPreviewResult(data);
      toast({
        title: 'Preview Generated',
        description: 'AI insight preview has been generated successfully',
      });
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate preview',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!selectedUser || !weekStarting) {
      toast({
        title: 'Missing Information',
        description: 'Please select a user and week starting date',
        variant: 'destructive',
      });
      return;
    }

    if (!navigator.onLine) {
      toast({
        title: 'Offline Mode',
        description: 'Sending test emails requires an internet connection',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('test-weekly-report', {
        body: JSON.stringify({
          user_id: selectedUser,
          report_type: previewType === 'premium' ? 'premium' : 'free',
          use_sample_data: useSampleData,
        }),
      });

      if (error) throw error;

      toast({
        title: 'Test Email Sent',
        description: 'Test email has been sent successfully',
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error',
        description: 'Failed to send test email',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h2 className="text-2xl font-semibold">Weekly Insight Preview</h2>
        <p className="text-muted-foreground mt-1">
          Preview and test weekly insight emails before they are sent to users
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Preview Controls</CardTitle>
          <CardDescription>
            Configure the preview settings and generate AI insights
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="user-select">Select User</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {user && (
                    <SelectItem value={user.id}>
                      {user.email} (You)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Week Starting</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !weekStarting && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {weekStarting ? format(weekStarting, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={weekStarting}
                    onSelect={setWeekStarting}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preview-type">Preview Type</Label>
              <Select value={previewType} onValueChange={(value: 'premium' | 'free') => setPreviewType(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="premium">Insight (Premium)</SelectItem>
                  <SelectItem value="free">Summary (Free)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="model-select">Model</Label>
              <Select value={model} onValueChange={(value: 'gpt-4o' | 'gpt-4o-mini') => setModel(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                  <SelectItem value="gpt-4o-mini">GPT-4o-mini</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="sample-data"
              checked={useSampleData}
              onCheckedChange={setUseSampleData}
            />
            <Label htmlFor="sample-data">Use Sample Data</Label>
          </div>

          <div className="flex gap-4 pt-4">
            <Button 
              onClick={handleGeneratePreview} 
              disabled={isGenerating || !selectedUser || !weekStarting}
              className="flex-1"
            >
              {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate Preview
            </Button>
            <Button 
              onClick={handleSendTestEmail} 
              disabled={isSending || !selectedUser || !weekStarting}
              variant="outline"
              className="flex-1"
            >
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Test Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {previewResult && (
        <Card>
          <CardHeader>
            <CardTitle>Generated Preview</CardTitle>
            <CardDescription>
              Preview type: {previewResult.previewType} | Model: {previewResult.model} | 
              Tokens: {previewResult.tokenUsage} | Cost: ${previewResult.estimatedCost.toFixed(4)}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">AI Generated Content:</Label>
              <div className="bg-muted p-4 rounded-md">
                <pre className="whitespace-pre-wrap text-sm">
                  {previewResult.aiOutput}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
